<template>
  <div
    :class="['tkPulldown', { '-disabled': disabled }]"
    :style="styleVars"
  >
    <select
      :value="selectedValue"
      :class="[
        'tkPulldown_pulldown',
        `-${iconPosition}`,
        `-${theme}`,
        `-${textColor}`,
        { '-putTextIconOutside': putTextIconOutside },
        { '-noIcon': !iconName },
        { '-noBorder': noBorder },
        { '-bold': bold },
        { '-active': active },
      ]"
      :disabled="disabled"
      @change="$emit('change', $event.target.value)"
    >
      <option
        v-if="unselectedLabel"
        value="unselected-value"
        class="tkPulldown_unselectedOption"
      >
        {{ unselectedLabel }}
      </option>
      <option
        v-for="item in items"
        :key="item.value"
        :value="item.value"
        :class="[
          'tkPulldown_option',
          `-${theme}`,
        ]"
      >
        {{ item.label }}
      </option>
    </select>
    <tk-icon
      v-if="!!iconName"
      :class="[
        'tkPulldown_icon',
        `-${iconPosition}`,
        { '-putTextIconOutside': putTextIconOutside },
        icon ? `-${icon}` : '',
      ]"
      :name="iconName"
      :color="computedIconColor"
    />
    <tk-icon
      v-if="textIcon"
      :class="[
        'tkPulldown_textIcon',
        { '-putTextIconOutside': putTextIconOutside },
      ]"
      :name="textIcon"
      :color="textIconColor"
    />
    <div
      v-if="textIconSeparator"
      :class="['tkPulldown_separator', `-${textIconSeparator}`]"
    />
  </div>
</template>

<script>
import TkIcon from '../TkIcon/TkIcon.vue'
import { input } from '../../../mixins'

export default {
  name: 'TkPulldown',

  components: { TkIcon },

  mixins: [input],

  model: {
    // 要素が選択されると change(選択されたvalue) を emit する
    event: 'change',
  },

  props: {
    value: {
      // 選択要素の value 値
      type: String,
      default: '',
    },
    items: {
      // {
      //   label: オプションとして表示する文字列
      //   value: change イベントで選択要素として連携される ID
      // }
      // の配列
      type: Array,
      required: true,
    },
    unselectedLabel: {
      type: String,
      default: undefined,
    },

    // ---------------
    // 外観に関するもの

    // スタイルプリセット
    // default: 白を基調とした基本的なスタイル
    // flat: フラットデザイン
    // outlined: 黒背景白抜き文字
    theme: {
      type: String,
      default: 'default',
    },
    // 文字サイズと高さのプリセット
    // 'normal', 'large', 'x-large', 'xx-large', 'xxx-large' が指定可能
    // 高さはそれぞれ 30, 36, 40, 45, 50
    size: {
      type: String,
      default: 'normal',
    },
    noBorder: {
      type: Boolean,
      default: false,
    },
    bold: {
      type: Boolean,
      default: false,
    },
    // true で色を反転させる
    active: {
      type: Boolean,
      default: false,
    },
    // アイコン名
    // none | arrow | spin | filled-spin
    // none の場合は非表示
    icon: {
      type: String,
      default: 'arrow',
    },
    // アイコンの色指定
    iconColor: {
      type: String,
      default: undefined,
    },
    // アイコンを左右どちらに配置するか 'left' or 'right'
    iconPosition: {
      type: String,
      default: 'right',
    },
    cursor: {
      type: String,
      default: 'pointer',
    },
    disabled: {
      // true の場合は内部のセレクトボックスを disabled にする
      type: Boolean,
      default: false,
    },
    textColor: {
      type: String,
      default: undefined,
    },
    textIcon: {
      // テキスト横に表示するアイコン名
      type: String,
      default: undefined,
    },
    putTextIconOutside: {
      // アイコンの位置をボックスの外側にする
      type: Boolean,
      default: false,
    },
    textIconColor: {
      // テキスト横に表示するアイコンの色指定
      type: String,
      default: undefined,
    },
    textIconSeparator: {
      // テキスト横に表示するアイコンの区切り線
      type: String,
      default: undefined,
    },
  },

  computed: {
    selectedValue() {
      const correspondingOption = this.items
        .find(i => i.value || i.value === 0
          ? String(i.value) === this.value
          : !this.value)
      return correspondingOption ? correspondingOption.value : 'unselected-value'
    },
    iconClasses() {
      return [
        'tkPulldown_icon',
        this.icon ? `-${this.icon}` : '',
        this.iconPosition === 'right' ? '-right' : '-left',
      ]
    },
    iconName() {
      if (!this.icon) return null

      switch (this.icon) {
        case 'arrow':
          return 'arrow_left'
        case 'spin':
          return 'spin'
        case 'filled-spin':
          return 'filled_spin'
        default: return null
      }
    },
    // 現状、'active' が指定された時は例外なく白にしており、text-color="blue" のときくらいしか正常な見た目にならない
    // カラーごとに個別に対応するべき
    computedIconColor() {
      return this.active ? 'white' : this.iconColor
    },

    styleVars() {
      const vars = this.__sizingVars(this.size, this.textIcon, this.textIconSeparator, this.putTextIconOutside)
      return {
        ...vars,
        '--cursor': this.cursor,
        '--view-font-size': Number(vars['--font-size'].slice(0, -2))  // 'px' の前を切り取る
      }
    },
  },
}
</script>

<style scoped lang="scss">
.tkPulldown {
  position: relative;

  @media (max-width: 657px) {
    height: var(--height);
  }

  &.-disabled {
    opacity: .5
  }
}

.tkPulldown_pulldown {
  position: relative;
  width: 100%;
  background-image: none !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  // layout
  height: var(--height);
  padding-top: 0;
  padding-bottom: 0;
  &.-left {
    padding-left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + 16px);
    padding-right: 10px;
  }
  &.-right {
    padding-left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x));
    padding-right: 32px;
  }
  &.-noIcon {
    padding-left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x));
    padding-right: 10px;
  }
  &.-putTextIconOutside {
    width : calc(100% - var(--outside-icon-space));
    padding-left: var(--text-padding-x);
    left: var(--outside-icon-space);

    &.-left {
      padding-left: calc(var(--text-padding-x) + 16px);
      padding-right: 10px;
    }
  }

  // theme
  &.-default {
    border: solid 1px #d2d2d2;
    border-radius: 4px;
    box-sizing: border-box;
    box-shadow: inset 2px 2px 2px 2px rgba(51, 51, 51, 0.1);
    background-color: #fff;
  }
  &.-flat {
    border: solid 1px #bababa;
    border-radius: 4px;
    box-shadow: none;  // デフォルトの打消し
    background-color: #fff;
  }
  &.-outlined {
    border: none;
    border-radius: 4px;
    background-color: #333;
    color: #fff;
  }

  // color
  &.-blue {
    border-color: #BBD2E6;
    color: #1F6AAD;

    &.-active {
      background-color: #1F6AAD;
      color: #fff;
    }
  }

  &.-noBorder {
    border: unset;
  }

  &.-bold {
    font-weight: bold;
  }

  font-size: var(--font-size);
  @media (max-width: 657px) {
    // !important は外部 css の打ち消し
    // iphone の場合、16px 以上にしないと勝手にズームしてしまう
    // 16px未満の文字サイズを表示できるようにscale で調整する
    // scale では全体の大きさが変わってしまうため、変えたくない箇所にはscale の逆数を掛けて調整を打ち消す
    // --scale-factor で拡大／縮小の値を定義し、各調整に使用する
    --scale-factor: calc(var(--view-font-size) / 16);
    font-size: 16px !important;
    height: calc(var(--height) / var(--scale-factor));
    width: calc(100% / var(--scale-factor));
    transform-origin: top left;
    transform: scale(var(--scale-factor));

    &.-left {
      padding-left: calc((var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + 16px) / var(--scale-factor));
      padding-right: calc(10px / var(--scale-factor));
    }
    &.-right {
      padding-left: calc((var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x)) / var(--scale-factor));
      padding-right: calc(32px / var(--scale-factor));
    }
    &.-noIcon {
      padding-left: calc((var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x)) / var(--scale-factor));
      padding-right: calc(10px / var(--scale-factor));
    }
    &.-putTextIconOutside {
      width : calc((100% - var(--outside-icon-space)) / var(--scale-factor));
      padding-left: calc(var(--text-padding-x) / var(--scale-factor));
    }
  }

  cursor: var(--cursor);
}

.tkPulldown_icon {
  position: absolute;
  top: 0;
  bottom: 0;
  margin: auto;

  // size for each icon
  &.-arrow {
    --icon-width: 8px;
    transform: rotate(-90deg);
  }
  &.-spin {
    --icon-width: 10px;
  }
  &.-filled-spin {
    --icon-width: 10px;
  }
  width: var(--icon-width);

  &.-left {
    left: calc(var(--icon-padding-x) + var(--icon-size) + var(--separator-space) + var(--text-padding-x) + 10px - var(--icon-width));

    &.-putTextIconOutside {
      left: calc(var(--text-padding-x) + 10px - var(--icon-width) + var(--outside-icon-space));
    }
  }
  &.-right {
    right: calc(var(--icon-width) + 7px);
  }

  cursor: var(--cursor);
}

.tkPulldown_unselectedOption {
  display: none;
}

.tkPulldown_option {
  display: block;
  min-height: 1.2em;
  padding: 0px 2px 1px;
  font-weight: normal;
  white-space: nowrap;

  &.-default {
    color: #000;
  }
  &.-flat {
    color: #000;
  }
  &.-outlined {
    color: #fff;
  }
}

.tkPulldown_textIcon {
  position: absolute;
  top: var(--icon-padding-y);
  left: var(--icon-padding-x);
  width: var(--icon-size);
  height: var(--icon-size);
  cursor: var(--cursor);

  &.-putTextIconOutside {
    cursor: auto;
  }
}

.tkPulldown_separator {
  position: absolute;
  z-index: 1;
  cursor: var(--cursor);

  &.-default {
    top: calc(var(--height) * 0.2);
    left: calc(var(--icon-size) + var(--icon-padding-x) * 1.5);
    width: 0.5px;
    height: calc(var(--height) * 0.6);
    background-color: #dfe6e6;
  }
}
</style>
